import { useState, useRef, useEffect, useLayoutEffect } from "react";

/**
 * Manages the resizable tools dock panel width and the sticky toolbar height.
 */
export default function useDockResize({ isMobile, deps = [] }) {
  // Default to ~40% of the viewport (≈ a 60/40 split); the operator can still
  // drag the dock handle to adjust between 380px and 60% of the window.
  const [dockWidth, setDockWidth] = useState(() => {
    const w = (typeof window !== "undefined" ? window.innerWidth : 1280) * 0.4;
    return Math.round(Math.max(380, Math.min(980, w)));
  });
  const [isResizing, setIsResizing] = useState(false);
  const [stickyToolbarHeight, setStickyToolbarHeight] = useState(0);
  const resizeRef = useRef({ startX: 0, startWidth: 540 });
  const stickyToolbarRef = useRef(null);

  useEffect(() => {
    if (!isResizing) return;

    const onMouseMove = (e) => {
      const delta = resizeRef.current.startX - e.clientX;
      const nextWidth = resizeRef.current.startWidth + delta;
      const min = 380;
      const max = Math.min(window.innerWidth * 0.6, 980);
      setDockWidth(Math.max(min, Math.min(max, nextWidth)));
    };

    const onMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing]);

  useLayoutEffect(() => {
    const node = stickyToolbarRef.current;
    if (!node) return;

    const updateHeight = () => {
      setStickyToolbarHeight(node.getBoundingClientRect().height || 0);
    };

    updateHeight();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => updateHeight());
      observer.observe(node);
      window.addEventListener("resize", updateHeight);
      return () => {
        observer.disconnect();
        window.removeEventListener("resize", updateHeight);
      };
    }

    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [isMobile, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps

  const startResize = (e) => {
    resizeRef.current = { startX: e.clientX, startWidth: dockWidth };
    setIsResizing(true);
  };

  return {
    dockWidth,
    isResizing,
    stickyToolbarHeight,
    stickyToolbarRef,
    startResize,
  };
}
